import * as React from "react";
import { cn } from "@aisquare/ui";
import { SearchIcon, XIcon } from "lucide-react";
import { LATEST_VERSION, NAV } from "./registry";
import { hrefForComponent, hrefForLayer, type Route } from "./router";

/**
 * Component navigation.
 *
 * Layer → category → component, the same taxonomy the registry defines, so the
 * nav and the pages can't disagree about where something lives. The filter is
 * local: it narrows the tree in place rather than navigating, so you can scan
 * for a name without losing the page you are on.
 */
export function Sidebar({
  route,
  className,
  onNavigate,
}: {
  route: Route;
  className?: string;
  /** Called after a link is followed — lets the mobile drawer close itself. */
  onNavigate?: () => void;
}) {
  const [query, setQuery] = React.useState("");
  const navRef = React.useRef<HTMLElement>(null);
  const q = query.trim().toLowerCase();

  const filtered = React.useMemo(() => {
    if (!q) return NAV;
    return NAV.map((layer) => ({
      ...layer,
      categories: layer.categories
        .map((c) => ({
          ...c,
          entries: c.entries.filter(
            (e) =>
              e.title.toLowerCase().includes(q) ||
              e.id.includes(q) ||
              c.category.toLowerCase().includes(q),
          ),
        }))
        .filter((c) => c.entries.length > 0),
    })).filter((layer) => layer.categories.length > 0);
  }, [q]);

  const matches = filtered.reduce(
    (n, l) => n + l.categories.reduce((m, c) => m + c.entries.length, 0),
    0,
  );

  const activeId = route.kind === "component" ? route.id : null;

  // Keep the current component visible in the independently scrolling nav —
  // otherwise landing on a deep link leaves the nav parked at the top with no
  // indication of where you are in a 118-entry list.
  React.useEffect(() => {
    if (!activeId || !navRef.current) return;
    navRef.current
      .querySelector(`[data-nav-id="${activeId}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [activeId]);

  return (
    <div className={cn("flex h-full min-h-0 flex-col", className)}>
      <div className="shrink-0 px-4 pb-3 pt-4">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter components…"
            aria-label="Filter components"
            className={cn(
              "h-9 w-full rounded-lg border border-input bg-background ps-8 pe-8 text-label text-foreground outline-none",
              "transition-colors duration-180 placeholder:text-muted-foreground",
              "focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20",
            )}
          />
          {query ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear filter"
              className="absolute end-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground transition-colors hover:text-foreground"
            >
              <XIcon className="size-3.5" />
            </button>
          ) : null}
        </div>
      </div>

      <nav
        ref={navRef}
        aria-label="Component navigation"
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-12"
      >
        {filtered.map((layer) => (
          <div key={layer.meta.id} className="mb-5">
            <a
              href={hrefForLayer(layer.meta.id)}
              onClick={onNavigate}
              className={cn(
                "flex items-center justify-between gap-2 rounded-md px-2 py-1.5 transition-colors",
                route.kind === "layer" && route.id === layer.meta.id
                  ? "text-accent"
                  : "text-foreground hover:text-accent",
              )}
            >
              <span className="font-mono text-eyebrow uppercase">{layer.meta.label}</span>
              <span className="font-mono text-[10px] text-muted-foreground">{layer.total}</span>
            </a>

            {layer.categories.map((c) => (
              <div key={c.category} className="mt-2.5">
                <div className="mb-1 px-2 font-mono text-[10px] uppercase tracking-wider text-muted-foreground/60">
                  {c.category}
                </div>
                <ul>
                  {c.entries.map((entry) => {
                    const active = entry.id === activeId;
                    return (
                      <li key={entry.id}>
                        <a
                          href={hrefForComponent(entry.id)}
                          data-nav-id={entry.id}
                          onClick={onNavigate}
                          aria-current={active ? "page" : undefined}
                          className={cn(
                            "relative flex items-center gap-2 rounded-md py-1.5 ps-3 pe-2 text-label transition-colors duration-180",
                            active
                              ? "bg-accent/10 font-semibold text-accent"
                              : "text-foreground/80 hover:bg-muted hover:text-foreground",
                          )}
                        >
                          {active ? (
                            <span
                              aria-hidden
                              className="absolute inset-y-1.5 start-0 w-[2px] rounded-full bg-accent"
                            />
                          ) : null}
                          <span className="truncate">{entry.title}</span>
                          {entry.since === LATEST_VERSION ? (
                            <span
                              title={`Added in ${LATEST_VERSION}`}
                              className="ms-auto size-1.5 shrink-0 rounded-full bg-success"
                            />
                          ) : null}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        ))}

        {q && matches === 0 ? (
          <p className="px-2 py-6 text-caption text-muted-foreground">Nothing matches “{query}”.</p>
        ) : null}
      </nav>
    </div>
  );
}
